/**
 * The school student's tests -- /api/my_test (Phase 4, task 4.5).
 *
 * `id` everywhere is the ASSIGNMENT id. A student never handles an attempt
 * id; the API works out which attempt is theirs from the token.
 *
 * Errors: the API answers `{status, message}` with a sentence written for the
 * student ("This test opens at ...", "Time is up ..."). It is thrown as an
 * Error with that message, so screens show it as it is. A 428 means the
 * school's temporary password has not been replaced; the browser is sent to
 * the page that does that.
 */
import jwtAuthService from '../components/jwtAuthService.js';
import { API_BASE_URL } from '../components/apiConfig.js';

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function call<T = any>(method: 'GET' | 'POST', path: string, body?: any): Promise<{ result: T; server_time?: string; message: string }> {
  const token = await (jwtAuthService as any).getValidAccessToken();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  let json: any = null;
  try {
    json = await res.json();
  } catch {
    throw new ApiError(`The server answered ${res.status}. Please try again.`, res.status);
  }
  if (res.status === 428) {
    sessionStorage.setItem('wiziai_must_change_password', 'true');
    window.location.assign('/set-password');
  }
  if (!json || json.status !== 'success') {
    throw new ApiError((json && json.message) || `The server answered ${res.status}.`, res.status);
  }
  return json;
}

// ---------------------------------------------------------------------------
// Shapes, as My_test.php returns them
// ---------------------------------------------------------------------------

export type WindowState = 'upcoming' | 'open' | 'closed';
export type StudentState = 'assigned' | 'in_progress' | 'submitted' | 'auto_submitted' | 'missed' | 'excused';
export type AnswerStatus = 'not_attempted' | 'answered' | 'skipped' | 'marked_review' | 'answered_marked_review';

export interface MyTest {
  assignment_id: number;
  title: string;
  quiz_source: 'school' | 'global';
  instructions: string | null;
  scheduled_start: string;
  scheduled_end: string;
  duration_minutes: number;
  late_start_minutes: number | null;
  max_attempts: number;
  attempts_used: number;
  window_state: WindowState;
  state: StudentState;
  in_progress: boolean;
  can_start: boolean;
  cannot_start_reason: string | null;
  expires_at: string | null;
  seconds_left: number | null;
  submitted_at: string | null;
  show_results_mode: 'immediately' | 'after_window' | 'manual' | 'never';
  results_visible: boolean;
  results_available_at: string | null;
  score: number | null;
  percentage: number | null;
}

export interface QuestionContent {
  question_img_url: string;
  question_text: string | null;
  option_count: number;
  has_multiple_answer: number;
  option_a: string | null;
  option_b: string | null;
  option_c: string | null;
  option_d: string | null;
  question_type: 'mcq' | 'integer';
  solution?: string | null;
}

export interface PaperQuestion {
  id: number;
  question_order: number;
  marks: number;
  negative_marks: number;
  question_type: 'mcq' | 'integer';
  user_answer: string | null;
  status: AnswerStatus;
  time_spent: number;
  content: QuestionContent | null;
}

export interface Paper extends MyTest {
  attempt: { id: number };
  questions: PaperQuestion[];
  server_time: string;
}

export interface ResultQuestion extends PaperQuestion {
  correct_answer: string;
  is_correct: boolean;
  marks_obtained: number;
}

export interface MyTestDetail extends MyTest {
  server_time: string;
  result: null | {
    attempt_id: number;
    score: number;
    percentage: number;
    totals: {
      total_questions: number;
      answered: number;
      correct: number;
      incorrect: number;
      total_score: number;
      total_marks: number;
      percentage: number;
    };
    questions: ResultQuestion[];
  };
}

export const myTestApi = {
  list: () => call<MyTest[]>('GET', 'my_test'),
  get: (id: number) => call<MyTestDetail>('GET', `my_test/${id}`),
  start: (id: number) => call<Paper>('POST', `my_test/start/${id}`, {}),
  paper: (id: number) => call<Paper>('GET', `my_test/paper/${id}`),
  answer: (id: number, body: { question_user_id: number; user_answer: string | null; status: AnswerStatus; time_spent: number }) =>
    call<{ question_user_id: number; status: AnswerStatus; saved: boolean }>('POST', `my_test/answer/${id}`, body),
  submit: (id: number) => call<MyTestDetail>('POST', `my_test/submit/${id}`, {}),
  changePassword: (existing_password: string, new_password: string) =>
    call('POST', 'user/update_password', { existing_password, new_password }),
};

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export const showTime = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

/** "2" -> "B" for multiple choice; an integer answer as it is. */
export const answerLabel = (answer: string | null | undefined, type?: string) => {
  if (answer === null || answer === undefined || answer === '') return '—';
  if (type === 'integer') return answer;
  return answer
    .split(',')
    .map(a => {
      const n = parseInt(a.trim(), 10);
      return n >= 1 && n <= 4 ? 'ABCD'[n - 1] : a.trim();
    })
    .join(', ');
};
